import { ReactNode, useState } from 'react';
import { ArrowDown, Edit, Clone, Delete } from '../Icons';
import {
  AccordionHeader,
  AccordionTitle,
  AccordionArrow,
  AccordionContent,
  BenchmarkMetadataAccordion,
  AccordionDescription,
  AccordionActions,
} from './BenchmarkMetadataStyles';
import {
  Dots,
  ActionsWrapper,
  ActionsItem,
  ActionsItemIcon,
  ActionsItemText,
  Dot,
} from 'components/Table/helpers/CellActions/CellActionsStyled';
import { Dropdown } from 'react-bootstrap';
import { CloneModal } from './components/CloneModal/CloneModal';
import Tooltip from 'components/Tooltip/Tooltip';
import { EditModal } from './components/EditModal/EditModal';
import { DeleteModal } from './components/DeleteModal/DeleteModal';
import { Benchmark, BenchmarkTypes } from 'types/Benchmark';
import { staticWeightedValidationSchema } from 'modules/BenchmarkStaticWeighted/StaticWeightedValidationScheme';
import { absoluteReturnValidationSchema } from 'modules/AbsoluteReturn/AbsoluteReturnValidationScheme';
import { assetWeightedValidationSchema } from 'modules/BenchmarkAssetWeighted/AssetWeightedValidationScheme';
import { compositeWeightedValidationSchema } from 'modules/BenchmarkCompositeWeighted/CompositeWeightedValidationScheme';
import { CompositeReturnValidationScheme } from 'modules/CompositeReturn/CompositeReturnValidationScheme';
import { pullForwardValidationScheme } from 'modules/PullForward/PullForwardValidationScheme';
import { validationMode } from 'helpers/form';
import { useDataEnvironment } from 'contexts/DataEnvironmentContext';

type Properties = {
  children: ReactNode;
  isOpen?: boolean;
  isTimeWindowsScreen?: boolean;
  benchmark: Benchmark;
  onConfirm: () => void;
};

export const BenchmarkMetadata = ({
  isOpen,
  benchmark,
  isTimeWindowsScreen,
  children,
  onConfirm,
}: Properties) => {
  const [isExpanded, setExpanded] = useState(isOpen ?? false);
  const [showMenu, setShowMenu] = useState(false);
  const [isEditModalVisible, setEditModalVisible] = useState(false);
  const [isCloneModalVisible, setCloneModalVisible] = useState(false);
  const [isDeleteModalVisible, setDeleteModalVisible] = useState(false);
  const [dataEnvironment] = useDataEnvironment();

  let validationScheme;
  switch (benchmark.type?.id) {
    case BenchmarkTypes.AbsoluteReturn:
      validationScheme = absoluteReturnValidationSchema(validationMode.Edit);
      break;

    case BenchmarkTypes.AssetWeighted:
      validationScheme = assetWeightedValidationSchema(validationMode.Edit);
      break;

    case BenchmarkTypes.CompositeReturn:
      validationScheme = CompositeReturnValidationScheme(validationMode.Edit);
      break;

    case BenchmarkTypes.CompositeWeighted:
      validationScheme = compositeWeightedValidationSchema(validationMode.Edit);
      break;

    case BenchmarkTypes.PullForward:
      validationScheme = pullForwardValidationScheme(validationMode.Edit);
      break;

    case BenchmarkTypes.StaticWeighted:
      validationScheme = staticWeightedValidationSchema(validationMode.Edit);
      break;

    default:
      validationScheme = undefined;
      break;
  }

  const DotMenu = () => {
    return (
      <Dropdown
        className='d-block d-sm-none d-none d-sm-block d-md-block d-lg-none'
        onClick={() => showMenu && setShowMenu(false)}
      >
        <Dots onClick={() => setShowMenu(!showMenu)}>
          <Dot />
          <Dot />
          <Dot />
        </Dots>
        {showMenu && (
          <ActionsWrapper>
            <ActionsItem
              onClick={() => {
                setEditModalVisible(true);
              }}
            >
              <ActionsItemIcon>
                <Edit size='14px' fill='#0076C0' />
              </ActionsItemIcon>
              <ActionsItemText>Edit</ActionsItemText>
            </ActionsItem>
            <ActionsItem onClick={() => setCloneModalVisible(true)}>
              <ActionsItemIcon>
                <Clone size='14px' fill='#0076C0' />
              </ActionsItemIcon>
              <ActionsItemText>Clone</ActionsItemText>
            </ActionsItem>
            <ActionsItem
              onClick={() => {
                setDeleteModalVisible(true);
              }}
            >
              <ActionsItemIcon>
                <Delete size='14px' fill='#0076C0' />
              </ActionsItemIcon>
              <ActionsItemText>Delete</ActionsItemText>
            </ActionsItem>
          </ActionsWrapper>
        )}
      </Dropdown>
    );
  };

  let lastModifiedDate = null;

  if (benchmark?.lastModifiedDate) {
    const dateString = new Date(benchmark.lastModifiedDate);
    let dateValue = dateString.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });

    const parts = dateValue.split(' ');
    dateValue = `${parts[0]}. ${parts[1]} ${parts[2]}`;

    let timeValue = dateString.toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
      timeZoneName: 'short',
    });

    lastModifiedDate = `Last Updated: ${dateValue} at ${timeValue}`;
    }

  return isTimeWindowsScreen ? (
    <BenchmarkMetadataAccordion isOpen={isExpanded}>
      <AccordionHeader>
        <AccordionTitle>
          <Tooltip message={benchmark?.name ?? ''} delay={300}>
            <span data-testid='accordion-benchmark-name'>
              {benchmark?.name}
            </span>
          </Tooltip>
        </AccordionTitle>
        <AccordionDescription>{lastModifiedDate}</AccordionDescription>
        <AccordionActions>
          <AccordionArrow
            data-testid='expandArrow'
            onClick={() => setExpanded(!isExpanded)}
          >
            <ArrowDown size={'14px'} fill={'black'} />
          </AccordionArrow>
        </AccordionActions>
      </AccordionHeader>
      <AccordionContent>
        <div className='container-fluid'>{children}</div>
      </AccordionContent>
    </BenchmarkMetadataAccordion>
  ) : (
    <BenchmarkMetadataAccordion isOpen={isExpanded}>
      <AccordionHeader>
        <AccordionTitle>
          <Tooltip message={benchmark?.name ?? ''} delay={300}>
            <span data-testid='accordion-benchmark-name'>
              {benchmark?.name}
            </span>
          </Tooltip>
        </AccordionTitle>
        <AccordionDescription>{lastModifiedDate}</AccordionDescription>
        <AccordionActions>
        <div className='d-none d-sm-none d-md-none d-lg-block'>

            {benchmark?.portfolioAnalyticsResourceId ? (
                <a
                    href={`${dataEnvironment?.url}/JumpTo/GoToAsset?ResourceId=${benchmark?.portfolioAnalyticsResourceId}`}
                    className='link-underline link-underline-opacity-0'
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid='entityLink'
                >
                    Mapped Entity
                </a>
            ) : (
                <span className="text-muted" data-testid='entityLink'>No Mapped Entity</span>
            )}
            <button
                className='btn btn-link link-underline link-underline-opacity-0'
                data-testid='editBenchmarkButton'
                onClick={() => setEditModalVisible(true)}
            >
                <Edit size={'14px'} />
                &nbsp;&nbsp;Edit
            </button>
            <button
              className='btn btn-link link-underline link-underline-opacity-0'
              data-testid='cloneBenchmarkButton'
              onClick={() => setCloneModalVisible(true)}
            >
              <Clone size={'14px'} />
              &nbsp;&nbsp;Clone
            </button>
            <button
              className='btn btn-link link-underline link-underline-opacity-0'
              data-testid='deleteBenchmarkButton'
              onClick={() => setDeleteModalVisible(true)}
            >
              <Delete size={'14px'} />
              &nbsp;&nbsp;Delete
            </button>
          </div>
          <DotMenu />
          <AccordionArrow
            data-testid='expandArrow'
            onClick={() => setExpanded(!isExpanded)}
          >
            <ArrowDown size={'14px'} fill={'black'} />
          </AccordionArrow>
        </AccordionActions>
      </AccordionHeader>
      <AccordionContent>
        <div className='container-fluid'>{children}</div>
      </AccordionContent>
      <EditModal
        benchmark={benchmark}
        visible={isEditModalVisible}
        onClose={() => setEditModalVisible(false)}
        onConfirm={() => {
          setEditModalVisible(false);
          onConfirm();
        }}
        validationScheme={validationScheme}
      />
      <CloneModal
        benchmark={benchmark}
        visible={isCloneModalVisible}
        onClose={() => setCloneModalVisible(false)}
      />
      <DeleteModal
        benchmark={benchmark}
        visible={isDeleteModalVisible}
        onClose={() => setDeleteModalVisible(false)}
      />
    </BenchmarkMetadataAccordion>
  );
};
